import { Grid, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import BarChart from "../../components/charts/BarChart";
import SideInfo from "./SideInfo";
import AppCard from "../../components/cards/AppCard";
import { PeriodFilter } from "../../common/enums/common.enums";
import { convertChartData } from "../../common/helpers/chart.helper";
import { convertStatisticData } from "../../common/helpers/statistics.helper";
import { IChart, IChartInfo } from "../../common/interfaces/chart.interface";
import { IStatistics } from "../../common/interfaces/statistic.interface";
import { useTabContext } from "../../context/tabs/TabContext";
import {
  geTreasuryOverview,
  getBuyChartInfo,
  getSellChartInfo,
} from "../../api/platform/platform.treasuries";
import { ITreasuryOverview } from "../../common/interfaces/treasury.interface";
import { round } from "../../utils/helpers";

const PlatformVaults: FC = () => {
  const [treasuryOverview, setTreasuryOverview] = useState<ITreasuryOverview>();
  const [buyChartInfo, setBuyChartInfo] = useState<IChart[]>();
  const [sellChartInfo, setSellChartInfo] = useState<IChart[]>();
  const [buyStatisticInfo, setBuyStatisticInfo] = useState<IStatistics>();
  const [sellStatisticInfo, setSellStatisticInfo] = useState<IStatistics>();

  const [cardLoader, setCardLoader] = useState<boolean>(false);
  const [buyTab, setBuyTab] = useState<string>(PeriodFilter.DAILY);
  const [sellTab, setSellTab] = useState<string>(PeriodFilter.DAILY);

  const changeBuyTab = (tab: PeriodFilter): void => {
    setBuyTab(tab);
  };

  const changeSellTab = (tab: PeriodFilter): void => {
    setSellTab(tab);
  };

  useEffect(() => {
    void geTreasuryOverviewFromApi();
    void getBuyChartInfoFromApi();
    void getSellChartInfoFromApi();
  }, []);

  const geTreasuryOverviewFromApi = async () => {
    try {
      setCardLoader(true);
      setTreasuryOverview(await geTreasuryOverview());
      setCardLoader(false);
    } catch (error) {
      console.log(error);
    }
  };

  const getBuyChartInfoFromApi = async () => {
    try {
      const response: IChartInfo = await getBuyChartInfo();
      console.log(response);
      const convertedChartData: IChart[] | undefined =
        convertChartData(response);
      setBuyChartInfo(convertedChartData);
      const statisticData = convertStatisticData(response);
      setBuyStatisticInfo(statisticData);
    } catch (error) {
      console.log(error);
    }
  };

  const getSellChartInfoFromApi = async () => {
    try {
      const response: IChartInfo = await getSellChartInfo();
      console.log(response);
      const convertedChartData: IChart[] | undefined =
        convertChartData(response);
      setSellChartInfo(convertedChartData);
      const statisticData = convertStatisticData(response);
      setSellStatisticInfo(statisticData);
    } catch (error) {
      console.log(error);
    }
  };

  const activeFundraises = useMemo(() => {
    return treasuryOverview && treasuryOverview.activeFundraises;
  }, [treasuryOverview]);

  const totalNumberOfFundraises = useMemo(() => {
    return treasuryOverview && treasuryOverview.totalNumberOfFundraises;
  }, [treasuryOverview]);

  const totalRaisedAmount = useMemo(() => {
    return treasuryOverview && treasuryOverview.totalRaisedAmount;
  }, [treasuryOverview]);

  const totalTreasuryBalance = useMemo(() => {
    return treasuryOverview && treasuryOverview.totalTreasuryBalance;
  }, [treasuryOverview]);

  const totalNumberOfNFTs = useMemo(() => {
    return treasuryOverview && treasuryOverview.totalNumberOfNFTs;
  }, [treasuryOverview]);

  const totalFinancialAccountAmount = useMemo(() => {
    return treasuryOverview && treasuryOverview.totalFinancialAccountAmount;
  }, [treasuryOverview]);

  return (
    <div>
      <Grid
        container
        spacing={{ xs: 1, md: 2, sm: 2 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        <Typography
          variant="h4"
          component="span"
          key={`typography1`}
          sx={{
            display: "flex",
            alignItems: "center",
            padding: 1,
            width: "100%",
          }}
        >
          Treasury and vault
        </Typography>
        <Grid item xs={12} sm={12} md={3}>
          <AppCard
            label={"Active fundraises / Total fundraises"}
            value={`${activeFundraises} / ${totalNumberOfFundraises}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <AppCard
            label={"Total raised amount"}
            value={`${round(totalRaisedAmount)}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <AppCard
            label={"Total treasury balance"}
            value={`${round(totalTreasuryBalance)}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <AppCard
            label={"Total number of NFTs in Vault"}
            value={`${totalNumberOfNFTs}`}
            loader={cardLoader}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <AppCard
            label={"Total financial account amount"}
            value={`${round(totalFinancialAccountAmount)}`}
            loader={cardLoader}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={{ xs: 1, md: 2, sm: 2 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        <Grid item xs={12} sm={12} md={6}>
          <useTabContext.Provider
            value={{
              tab: buyTab,
              changeTab: changeBuyTab,
            }}
          >
            <SideInfo type={"buying NFTs"} statisticsData={buyStatisticInfo} />
            <BarChart chartData={buyChartInfo} />
          </useTabContext.Provider>
          <Typography
            variant="body1"
            component="span"
            key={`typography1`}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 1,
              width: "100%",
            }}
          >
            Total money spent buying NFTs
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <useTabContext.Provider
            value={{
              tab: sellTab,
              changeTab: changeSellTab,
            }}
          >
            <SideInfo
              type={"selling NFTs"}
              statisticsData={sellStatisticInfo}
            />
            <BarChart chartData={sellChartInfo} />
          </useTabContext.Provider>
          <Typography
            variant="body1"
            component="span"
            key={`typography1`}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 1,
              width: "100%",
            }}
          >
            Total money spent selling NFTs
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
};

export default PlatformVaults;
